/**
 * Dataset API Hooks
 *
 * TanStack Query hooks for dataset CRUD operations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listDatasetsApiV1DatasetsGet,
  createDatasetApiV1DatasetsPost,
  getDatasetApiV1DatasetsDatasetIdGet,
  updateDatasetApiV1DatasetsDatasetIdPatch,
  deleteDatasetApiV1DatasetsDatasetIdDelete,
} from '@/api';
import type { DatasetCreate, DatasetUpdate } from '@/api';
import { useAuthEnabled } from './useAuthQuery';

// Query keys factory
export const datasetKeys = {
  all: ['datasets'] as const,
  lists: () => [...datasetKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) =>
    [...datasetKeys.lists(), filters] as const,
  details: () => [...datasetKeys.all, 'detail'] as const,
  detail: (id: string) => [...datasetKeys.details(), id] as const,
};

/**
 * List all datasets for the current tenant.
 */
export function useDatasets(options?: { enabled?: boolean }) {
  const { enabled: authEnabled } = useAuthEnabled(options?.enabled ?? true);

  return useQuery({
    queryKey: datasetKeys.lists(),
    queryFn: async () => {
      const response = await listDatasetsApiV1DatasetsGet();
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to fetch datasets'
        );
      }
      return response.data;
    },
    enabled: authEnabled,
  });
}

/**
 * Get a single dataset by ID.
 */
export function useDataset(datasetId: string) {
  const { enabled: authEnabled } = useAuthEnabled(Boolean(datasetId));

  return useQuery({
    queryKey: datasetKeys.detail(datasetId),
    queryFn: async () => {
      const response = await getDatasetApiV1DatasetsDatasetIdGet({
        path: { dataset_id: datasetId },
      });
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to fetch dataset'
        );
      }
      return response.data;
    },
    enabled: authEnabled,
  });
}

/**
 * Create a new dataset.
 */
export function useCreateDataset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: DatasetCreate) => {
      const response = await createDatasetApiV1DatasetsPost({
        body: data,
      });
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to create dataset'
        );
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: datasetKeys.lists() });
    },
  });
}

/**
 * Update an existing dataset.
 */
export function useUpdateDataset(datasetId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: DatasetUpdate) => {
      const response = await updateDatasetApiV1DatasetsDatasetIdPatch({
        path: { dataset_id: datasetId },
        body: data,
      });
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to update dataset'
        );
      }
      return response.data;
    },
    onSuccess: (updatedDataset) => {
      queryClient.setQueryData(datasetKeys.detail(datasetId), updatedDataset);
      queryClient.invalidateQueries({ queryKey: datasetKeys.lists() });
    },
  });
}

/**
 * Delete a dataset.
 */
export function useDeleteDataset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (datasetId: string) => {
      const response = await deleteDatasetApiV1DatasetsDatasetIdDelete({
        path: { dataset_id: datasetId },
      });
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to delete dataset'
        );
      }
      return response.data;
    },
    onSuccess: (_, datasetId) => {
      queryClient.removeQueries({ queryKey: datasetKeys.detail(datasetId) });
      queryClient.invalidateQueries({ queryKey: datasetKeys.lists() });
    },
  });
}
